import { Component, computed, DestroyRef, inject } from '@angular/core';
import { combineLatest, tap } from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { TranslateModule } from '@ngx-translate/core';
import { ActivatedRoute, RouterLink } from '@angular/router';
import { PagedTableComponent } from 'common/table/paged-table.component';
import { AggregatedProductsDataSource } from 'component/aggregated-products-data-source';
import { DatePipe } from '@angular/common';
import {
  MatCell,
  MatColumnDef,
  MatHeaderCell,
  MatHeaderCellDef,
  MatTableModule,
} from '@angular/material/table';
import { ReactiveFormsModule } from '@angular/forms';
import { ProductsEditingSupport } from 'component/products-editing-support';
import { MatSortHeader } from '@angular/material/sort';
import { ProgressBarComponent } from 'common/common/progress-bar.component';
import { DescriptionComponent } from 'common/description/description.component';
import { ProductsFilteringSupport } from 'component/products-filtering-support';
import { ChipComponent } from 'common/designsystem/chip/chip.component';
import { CardHeaderComponent } from 'common/card/header/card-header.component';
import { AddProductComponent } from 'component/add/add-product.component';
import { MatDialog } from '@angular/material/dialog';
import { ApiManufacturer } from 'api';
import { IntakeTypeChipComponent } from 'common/designsystem/chip/intake-type-chip.component';
import { BillingStatusService } from 'common/billing/billing-status.service';
import { IconDirective } from 'common/icon/icon.directive';
import { ButtonDirective } from 'common/button/button.directive';

@Component({
  selector: 'comp-products',
  templateUrl: 'products.component.html',
  styleUrls: ['products.component.scss'],
  imports: [
    TranslateModule,
    ReactiveFormsModule,
    PagedTableComponent,
    MatColumnDef,
    MatHeaderCell,
    MatHeaderCellDef,
    MatCell,
    MatTableModule,
    DatePipe,
    MatSortHeader,
    RouterLink,
    ProgressBarComponent,
    DescriptionComponent,
    ChipComponent,
    CardHeaderComponent,
    IntakeTypeChipComponent,
    IconDirective,
    ButtonDirective,
  ],
})
export class ProductsComponent {
  private route = inject(ActivatedRoute);
  private destroyRef = inject(DestroyRef);
  readonly dialog = inject(MatDialog);
  private readonly billingStatusService = inject(BillingStatusService);

  dataSource = new AggregatedProductsDataSource();
  editingSupport = new ProductsEditingSupport();
  filteringSupport;
  private manufacturer: ApiManufacturer | null = null;

  public constructor() {
    this.filteringSupport = computed(() => {
      const customEnumFilters = this.dataSource.customEnumFilters();
      return new ProductsFilteringSupport(customEnumFilters);
    });
    combineLatest([this.route.data, this.route.queryParamMap])
      .pipe(
        tap(([data, urlQueryParamMap]) => {
          this.manufacturer = data['Manufacturer'];
          this.dataSource.manufacturerId = data['Manufacturer'].id;
          this.dataSource.refreshFromQueryParams(urlQueryParamMap);
          this.billingStatusService.refresh();
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();
  }

  protected async addProduct() {
    if (!this.manufacturer) {
      throw new Error('Manufacturer expected, but not found');
    }
    const dialogRef = await AddProductComponent.open(this.dialog, this.billingStatusService, {
      origin: 'products',
      manufacturer: this.manufacturer,
    });
    if (dialogRef == null) {
      return;
    }
    dialogRef.afterClosed().subscribe((newProduct) => {
      if (!newProduct?.id) {
        return;
      }

      this.dataSource.refreshFromQueryParams(this.route.snapshot.queryParamMap);
    });
  }

  protected addDisabled = computed(() => {
    return this.billingStatusService.status()?.isProductCreationPossible === false;
  });
}
